/**
 * React Query bindings for the Cal.com module (`cal-integration.service.ts`).
 * A write's answer is the agent's new connection (none, after a disconnect):
 * the card shows it at once, reads it again behind the scenes, and a toast
 * says what changed. A refusal is not toasted: the dialog that sent the write
 * shows it, under the field it is about or above its buttons, and stays open
 * with what was typed.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { ApiError, isNotFound } from "@/lib/api-client";
import { notFoundIsFinal } from "./assistant.query";
import {
  type CalIntegration,
  type CreateCalIntegrationBody,
  type UpdateCalIntegrationBody,
  createCalIntegration,
  deleteCalIntegration,
  getCalIntegration,
  updateCalIntegration,
} from "./cal-integration.service";

export type { CalIntegration } from "./cal-integration.service";

export const calIntegrationKey = (agentId?: string) => ["cal-integration", agentId] as const;

/**
 * The agent's connection, `null` when it has none. `/agents/new` has no agent
 * yet, so there is nothing to ask for; a deleted agent's 404 is final, as the
 * agent's own read is.
 */
export const useCalIntegration = (agentId?: string) =>
  useQuery<CalIntegration | null>({
    queryKey: calIntegrationKey(agentId),
    queryFn: () => getCalIntegration(agentId!),
    enabled: !!agentId && agentId !== "new",
    ...notFoundIsFinal,
  });

/**
 * A write to the connection of the agent `agentIdOf` names, answering that
 * agent's connection as it now stands. On success the card shows the answer
 * at once — the dialog closes onto it — and `done` is toasted. A refusal that
 * `outOfDate` recognises means someone changed the connection meanwhile: the
 * card drops what it showed and reads it again, and the words are still the dialog's.
 */
function useCalWrite<Vars>(
  write: (vars: Vars) => Promise<CalIntegration | null>,
  agentIdOf: (vars: Vars) => string,
  done: string,
  outOfDate: (error: Error) => boolean,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const reread = (vars: Vars) =>
    queryClient.invalidateQueries({ queryKey: calIntegrationKey(agentIdOf(vars)) });

  return useMutation({
    mutationFn: write,
    onSuccess: (connection: CalIntegration | null, vars: Vars) => {
      queryClient.setQueryData(calIntegrationKey(agentIdOf(vars)), connection);
      void reread(vars);
      toast({ title: done });
    },
    onError: (error: Error, vars: Vars) => {
      // What the card shows is out of date, so it is dropped and read again: a read that then
      // fails shows as a failed read, as in the Angular dashboard, not as the stale answer.
      if (outOfDate(error)) void queryClient.resetQueries({ queryKey: calIntegrationKey(agentIdOf(vars)) });
    },
  });
}

/**
 * A 409: another tab or person connected this agent meanwhile, and the card reads theirs. A 404
 * ("Agent not found"): the agent was deleted, also while Cal.com was being asked, and the card
 * reads that it is gone.
 */
const connectedMeanwhileOrGone = (error: Error) =>
  (error instanceof ApiError && error.status === 409) || isNotFound(error);

export const useCreateCalIntegration = () =>
  useCalWrite(
    createCalIntegration,
    (body: CreateCalIntegrationBody) => body.agent_id,
    "Cal.com connected",
    connectedMeanwhileOrGone,
  );

/**
 * A 404 ("Cal.com connection not found") is a connection someone disconnected
 * meanwhile, or deleted with its agent: the card reads which.
 */
export const useUpdateCalIntegration = () =>
  useCalWrite(
    ({ connection, updates }: { connection: CalIntegration; updates: UpdateCalIntegrationBody }) =>
      updateCalIntegration(connection.id, updates),
    ({ connection }) => connection.agent_id,
    "Cal.com connection updated",
    isNotFound,
  );

/** Answers no connection: the agent has none once this succeeds. A 404 is read as a change's is. */
export const useDeleteCalIntegration = () =>
  useCalWrite(
    async (connection: CalIntegration) => {
      await deleteCalIntegration(connection.id);
      return null;
    },
    (connection) => connection.agent_id,
    "Cal.com disconnected",
    isNotFound,
  );
